import React, { useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useLocation } from 'react-router-dom';

import { Button, Divider, Input, Tabs } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { downloadBase64Pdf, formatDate } from 'helpers/functions';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import {
  StoreCommunicationMessageLogActionSlug,
  StoreCommunicationMessageLogOrigin
} from 'v2/interfaces/subscriptions';
import type { ValidateWhatsAppMessageResponse } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

type BadgeProps = {
  title: string;
  subtitle: string;
};

function Badge({ title, subtitle }: BadgeProps) {
  return (
    <li className='flex items-center gap-4'>
      <img src='/img/green-check-shield.svg' alt='Escudo verde com um símbolo de "check"' />
      <div>
        <p className='mb-0.5! text-sm font-semibold'>{title}</p>
        <p className='mb-0! text-sm font-normal'>{subtitle}</p>
      </div>
    </li>
  );
}

function Content() {
  const { search } = useLocation();
  const sentMessageId = new URLSearchParams(search).get('sentMessageId');
  const { data, isLoading, isError, error } = useQuery<ValidateWhatsAppMessageResponse>({
    queryKey: ['validateWhatsAppMessage', sentMessageId],
    queryFn: () => subscriptionsService.validateWhatsAppMessage(sentMessageId),
    enabled: !!sentMessageId,
    retry: false
  });
  const [didCopy, setDidCopy] = useState(false);
  const [didCopyBankslip, setDidCopyBankslip] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [userSelectedTab, setUserSelectedTab] = useState<'pix' | 'bankslip' | null>(null);
  const shouldSaveCopy = useRef(true);
  const shouldSaveCopyBankslip = useRef(true);
  const shouldSaveDownloadBankslip = useRef(true);
  const [downloading, setDownloading] = useState(false);

  const pix = data?.pix;
  const recoveryUrl = data?.recoveryUrl;
  const bankslip = data?.bankslip;
  const subscriptionPaymentMethod = data?.subscriptionPaymentMethod;
  const defaultTabFromApi = subscriptionPaymentMethod?.label === 'pix' ? 'pix' : 'bankslip';
  const currentTab = userSelectedTab ?? defaultTabFromApi;

  async function onCopyBankslip() {
    setDidCopyBankslip(true);
    setTimeout(() => setDidCopyBankslip(false), 3000);

    if (shouldSaveCopyBankslip.current) {
      try {
        await subscriptionsService.storeCommunicationMessageLog({
          id: sentMessageId,
          actionSlug: StoreCommunicationMessageLogActionSlug.COPIED,
          origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
          context: 'Boleto copiado',
          createdAt: new Date().toISOString()
        });
        shouldSaveCopyBankslip.current = false;
      } catch {
        console.warn('Error while saving bankslip copy log');
      }
    }
  }

  async function onCopy() {
    setDidCopy(true);
    setTimeout(() => setDidCopy(false), 3000);

    if (shouldSaveCopy.current) {
      try {
        await subscriptionsService.storeCommunicationMessageLog({
          id: sentMessageId,
          actionSlug: StoreCommunicationMessageLogActionSlug.COPIED,
          origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
          context: 'Pix copia e cola copiado',
          createdAt: new Date().toISOString()
        });
        shouldSaveCopy.current = false;
      } catch {
        console.warn('Error while saving pix copy log');
      }
    }
  }

  if (!sentMessageId) {
    return (
      <UnloggedLayout>
        <div className='mx-auto flex min-h-[70vh] flex-col items-center justify-center p-4 text-center'>
          <Icon.TriangleAlert className='text-secondary-600 mb-4 text-[[4rem]'  />
          <h2>Link inválido</h2>
          <p>Parâmetros inválidos. Por favor, verifique o link recebido.</p>
        </div>
      </UnloggedLayout>
    );
  }

  async function handleDownloadBankslip() {
    if (!bankslip?.pdfBase64) {
      alert('PDF não disponível. Tente novamente.');
      return;
    }

    const formattedDate = bankslip.invoiceDueDate ? formatDate(bankslip.invoiceDueDate) : '';
    const fileName = `boleto cobrança recorrente - venc ${formattedDate}.pdf`;

    try {
      setDownloading(true);
      downloadBase64Pdf(bankslip.pdfBase64, fileName);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);

      if (shouldSaveDownloadBankslip.current) {
        try {
          await subscriptionsService.storeCommunicationMessageLog({
            id: sentMessageId,
            actionSlug: StoreCommunicationMessageLogActionSlug.CLICKED,
            origin: StoreCommunicationMessageLogOrigin.REDIRECT_PAGE,
            context: 'Download boleto realizado',
            createdAt: new Date().toISOString()
          });
          shouldSaveDownloadBankslip.current = false;
        } catch {
          console.warn('Error while saving bankslip download log');
        }
      }
    } catch {
      alert('Erro ao baixar boleto. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return <GlobalLoading />;
  }

  const noPaymentDetails = !bankslip?.code || !bankslip?.pdfBase64 || !data;

  if (isError || noPaymentDetails) {
    console.error(error);
    const isAxiosError = error instanceof AxiosError;
    const defaultMessage = 'Erro ao carregar dados para pagamento.';
    return (
      <div className='mx-auto flex min-h-[70vh] flex-col items-center justify-center p-4 text-center'>
        <Icon.CircleAlert className='text-secondary-600 mb-4 text-[[4rem]'  />
        {isAxiosError ? (
          <h3>{error.response?.data?.data?.message ?? defaultMessage}</h3>
        ) : (
          <>
            <h3>Erro ao carregar os dados.</h3> <p>Por favor, tente novamente mais tarde.</p>
          </>
        )}
      </div>
    );
  }

  const tabItems = [
    {
      key: 'bankslip',
      label: 'Boleto',
      children: (
        <>
          <div className='mb-4 flex flex-col'>
            <h2 className='mb-0! text-base font-semibold'>Código de pagamento</h2>
            <p className='text-content-subtitle mb-0! text-base'>
              Se preferir, copie o código de pagamento abaixo, no seu banco selecione a opção Boleto e finalize sua
              compra.
            </p>
            <div className='my-6 flex flex-col'>
              <label className='text-content-subtitle mb-1 block text-sm'>Código de pagamento</label>
              <Input type='text' value={bankslip.code ?? ''} readOnly />
              <CopyToClipboard text={bankslip.code ?? ''} onCopy={onCopyBankslip}>
                <Button
                  className='mt-2!'
                  icon={didCopyBankslip ? <Icon.Check  /> : <Icon.Copy  />}
                  htmlType='button'
                  type='link'
                >
                  {didCopyBankslip ? 'Código copiado!' : 'Copiar código do Boleto'}
                </Button>
              </CopyToClipboard>
            </div>
          </div>
          <div className='flex w-full flex-wrap gap-2 max-md:flex-col'>
            <div className='flex flex-col'>
              <h3 className='mb-0! text-base font-semibold'>Baixar Boleto</h3>
              <p className='text-content-subtitle mb-0! text-sm'>Se preferir, baixe o boleto de pagamento abaixo:</p>
            </div>
          </div>
          <div className='mt-6 flex w-full justify-center'>
            <Button
              block
              onClick={handleDownloadBankslip}
              icon={downloadSuccess ? <Icon.Check  /> : <Icon.Download className='text-primary'  />}
              disabled={downloading}
            >
              {downloading ? 'Baixando...' : downloadSuccess ? 'Boleto baixado!' : 'Baixar Boleto'}
            </Button>
          </div>
        </>
      )
    },
    {
      key: 'pix',
      label: 'Pix',
      children: (
        <>
          <div className='mb-4 flex flex-col [&>p]:mt-2 [&>p]:text-base'>
            <h2 className='mb-0! text-base font-semibold'>Pagar com Pix Copia e Cola</h2>
            <p className='text-content-subtitle mb-0! text-sm'>
              Se preferir, copie o código Pix abaixo, no seu banco selecione a opção Pix Copia e Cola e finalize sua
              compra.
            </p>

            <div className='my-6 flex flex-col'>
              <label className='text-content-subtitle mb-1 block text-sm'>Código de pagamento</label>
              <Input type='text' value={pix.url} readOnly />
              <CopyToClipboard text={pix.url} onCopy={onCopy}>
                <Button
                  className='mt-2!'
                  icon={didCopy ? <Icon.Check  /> : <Icon.Copy  />}
                  htmlType='button'
                  type='link'
                >
                  {didCopy ? 'Código copiado!' : 'Copiar código Pix'}
                </Button>
              </CopyToClipboard>
            </div>
          </div>
          <div className='flex w-full flex-wrap gap-4 max-md:flex-col'>
            <div className='flex flex-col'>
              <h3 className='mb-0! text-base font-semibold'>Pagar com QR Code</h3>
              <p className='text-content-subtitle mb-0! text-sm'>Se preferir, pague com QR Code abaixo:</p>
              <ol className='mt-4! list-none [&_li]:mb-1 [&_li]:text-sm [&_li_span]:font-bold'>
                <li>1. Acesse sua conta bancária no Internet Banking ou através do app;</li>
                <li>2. Selecione a opção de pagar via Pix;</li>
                <li>3. Escaneie o código Pix ao lado e finalize o pagamento.</li>
              </ol>
            </div>
            <div className='border-outline-default bg-surface-default mx-auto flex items-center justify-center rounded-sm border p-2 [&_img]:-m-1 [&_img]:max-w-45'>
              <img src={pix.qrCodeBase64} alt='QR Code' />
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <div className='mx-auto flex max-w-160 flex-col items-center'>
      <Tabs
        className='mb-4 w-full'
        centered
        activeKey={currentTab}
        onChange={key => setUserSelectedTab(key as 'pix' | 'bankslip')}
        items={tabItems}
      />

      <div className='my-6 flex flex-col items-center text-sm'>
        <p className='text-content-subtitle mb-0! text-base'>Deseja utilizar outras formas de pagamento?</p>
        <a href={recoveryUrl} target='_blank' className='text-primary text-base' rel='noopener noreferrer'>
          Acesse aqui.
        </a>
      </div>

      <ul className='my-6 flex w-full flex-wrap items-center justify-around gap-4 max-md:m-0'>
        <Badge title='COMPRA' subtitle='100% SEGURA' />
        <Badge title='PRIVACIDADE' subtitle='PROTEGIDA' />
        <Badge title='GODADDY' subtitle='VERIFICADO E PROTEGIDO' />
      </ul>

      <div className='mt-4 flex w-full flex-col'>
        <div className='[&>a]:text-content-body flex items-center justify-center text-sm [&>a]:no-underline [&>a:hover]:underline'>
          <a href='https://www.eduzz.com/privacidade' target='_blank' rel='noopener noreferrer'>
            Política de privacidade
          </a>
          <Divider type='vertical' className='mx-2' />
          <a href='https://www.eduzz.com/termos' target='_blank' rel='noopener noreferrer'>
            Termos de compra
          </a>
          <Divider type='vertical' className='mx-2' />
          <a
            href='https://ajuda.eduzz.com/hc/pt-br/articles/1260806909249-Como-alterar-a-forma-de-pagamento-de-uma-assinatura'
            target='_blank'
            rel='noopener noreferrer'
          >
            Falar com suporte
          </a>
        </div>
      </div>
    </div>
  );
}

export function SubscriptionWhatsappPayment() {
  return (
    <UnloggedLayout>
      <Content />
    </UnloggedLayout>
  );
}
